// Pop-up presentation of the highlighted-dossier hero. Clicking any standing
// card in the shop opens this: the IDENTICAL <HighlightedDossier> hero for the
// clicked drop, purchase and all — just in a modal instead of swapping the
// admin-chosen page hero. Overlay behaviour matches the app's other modals
// (backdrop click + Escape close, dialog container takes initial focus).

import { useEffect, useRef } from "react";

import type { ShopDropResponse } from "./useShopDrops";
import { HighlightedDossier } from "./HighlightedDossier";

export interface HighlightedDossierModalProps {
  /** The clicked drop to present, or null when the modal is closed. */
  drop: ShopDropResponse | null;
  onClose: () => void;
}

export function HighlightedDossierModal({
  drop,
  onClose,
}: HighlightedDossierModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Escape-to-close, layered like CollectionItemDialog: when the nested
  // ShopBuyModal (a descendant of <HighlightedDossier>) is open, the first
  // Escape closes the buy step only — it is still in the DOM during this
  // synchronous keydown, so we bail and let its own handler run. A second
  // Escape, with the buy modal gone, closes this popup.
  useEffect(() => {
    if (!drop) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (document.querySelector('[data-testid="shop-buy-modal"]')) return;
      onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drop, onClose]);

  // Move focus into the dialog when it opens (parity with CollectionItemDialog).
  useEffect(() => {
    if (drop) panelRef.current?.focus();
  }, [drop]);

  if (!drop) return null;

  return (
    <div
      role="presentation"
      onClick={onClose}
      className="fixed inset-0 z-40 flex items-center justify-center overflow-y-auto bg-brand-forest-900/60 p-4"
      data-testid="shop-highlight-modal"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Highlighted dossier — ${drop.portfolio_title}`}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[820px] outline-none"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full text-lg text-brand-paper-100 opacity-70 hover:bg-brand-forest-700 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500"
        >
          ✕
        </button>
        <HighlightedDossier drop={drop} />
      </div>
    </div>
  );
}
